import type { Stories, RsuiteSortType } from '@@partner:types:app'
import type { ResponsiveTableColumn } from '@components/common/ResponsiveTable/ResponsiveTable'

import React from 'react'
import { formatDate } from '@@partner:helpers/util'

import { Link } from 'react-router-dom'
import { Button, Tag } from 'rsuite'
import CommonStatusLabel from '@components/common/CommonStatusLabel'
import ResponsiveTable from '@components/common/ResponsiveTable/ResponsiveTable'

import { NO_PHOTO_IMAGE } from '@@partner:config/image'

export interface StoriesTableProps {
  items?: Stories[]
  loading?: boolean
  sortColumn?: string
  sortType?: RsuiteSortType
  onSortColumn?: (dataKey: string, sortType?: RsuiteSortType) => void
  generateEditUrl?: (id: string | number) => string
}

const renderDateRange = (story: Stories) => {
  const { activeFrom, activeUntil } = story

  if (activeFrom && activeUntil) {
    return (
      <>
        <Tag>{formatDate(new Date(activeFrom))}</Tag>
        <span> / </span>
        <Tag>{formatDate(new Date(activeUntil))}</Tag>
      </>
    )
  }

  if (activeFrom && !activeUntil) {
    return (
      <>
        <span>С </span>
        <Tag>{formatDate(new Date(activeFrom))}</Tag>
      </>
    )
  }

  if (!activeFrom && activeUntil) {
    return (
      <>
        <span>До </span>
        <Tag>{formatDate(new Date(activeUntil))}</Tag>
      </>
    )
  }

  return <Tag color="red">Не задан</Tag>
}

const columns: ResponsiveTableColumn<Stories>[] = [
  {
    dataKey: 'id',
    header: 'ID',
    sortable: true,
    flexGrow: 1,
    isCardBadge: true
  },
  {
    dataKey: 'preview',
    header: 'Превью',
    flexGrow: 1,
    isCardImage: true,
    render: (story: Stories) => (
      <img
        src={story.preview?.url ?? NO_PHOTO_IMAGE}
        alt={story.name}
        style={{ 
          maxWidth: 80, 
          maxHeight: 80, 
          objectFit: 'contain',
          borderRadius: 4 
        }}
      />
    ),
    renderMobile: (story: Stories) => (
      <img
        src={story.preview?.url ?? NO_PHOTO_IMAGE}
        alt={story.name}
      />
    )
  },
  {
    dataKey: 'name',
    header: 'Название',
    flexGrow: 2,
    isCardTitle: true,
    render: (story: Stories) => <Tag color="blue">{story.name}</Tag>
  },
  {
    dataKey: 'status',
    header: 'Статус',
    sortable: true,
    flexGrow: 1,
    render: (story: Stories) => <CommonStatusLabel status={story.status} />
  },
  {
    dataKey: 'created_at',
    header: 'Создано',
    sortable: true,
    flexGrow: 2,
    hideOnMobile: true,
    render: (story: Stories) => (
      <Tag color="blue">{formatDate(new Date(story.created_at))}</Tag>
    )
  },
  {
    dataKey: 'activeFrom',
    header: 'Срок действия',
    sortable: true,
    flexGrow: 2,
    render: renderDateRange
  }
]

export default function StoriesTable ({
  items = [],
  loading = false,
  sortColumn,
  sortType,
  onSortColumn,
  generateEditUrl
}: StoriesTableProps) {
  const renderActions = (story: Stories) => (
    <>
      {generateEditUrl && (
        <Button
          appearance="primary"
          color="cyan"
          as={Link}
          to={generateEditUrl(story.id)}
          size="xs"
        >
          Изменить
        </Button>
      )}
    </>
  )

  return (
    <ResponsiveTable<Stories>
      data={items}
      columns={columns}
      loading={loading}
      rowKey="id"
      rowHeight={100}
      sortColumn={sortColumn}
      sortType={sortType}
      onSortColumn={onSortColumn}
      renderActions={renderActions}
      emptyMessage="Истории не найдены"
    />
  )
}
